import { useRef, useState, useEffect } from 'react';
import { Toast } from 'primereact/toast';
import { Toolbar } from 'primereact/toolbar';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { Dialog } from 'primereact/dialog';
import { Password } from 'primereact/password';
import { RadioButton } from 'primereact/radiobutton';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import moment from 'moment';
import { NavLink, useNavigate } from 'react-router-dom';
import { Badge } from 'primereact/badge';
import { Tooltip } from 'primereact/tooltip';
import { Check, Copy } from 'lucide-react';

import AppBreadcrumb from '../../components/CustomeBreadcrumb/AppBreadcrumb';
import { CompanyListColumns } from '../../appconfig/DatatableSetting';
import { ADD_COMPANY_DETAILS, LIST_COMPANIES, EDIT_COMPANY_DETAILS, DELETE_COMPANY } from '../../config/ApiConstant';
import { postAllData, getAllDetails, updateData, deleteData } from '../../service/ApiService';
import { validateCompanyForm } from '../../config/Validate';
import {
    CompanyFilingStatusOptions,
    defaultPageRowOptions,
    defaultRowOptions,
    gstOption,
    paginatorLinks,
    showingEntries,
    SkeletonbodyTemplate,
    Skeletonitems,
    WCBOptions,
    PendingFiledStatusOptions
} from '../../appconfig/Settings';

const CompanyList = () => {
    const toast = useRef<any>(null);
    const navigate = useNavigate();
    const dataTableRef = useRef<any>(null);
    const topScrollRef = useRef<HTMLDivElement>(null);

    const storagePrefix = 'companyList';

    const emptyFilters = {
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        company_name: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
        email: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
        tax_status: { value: null, matchMode: FilterMatchMode.EQUALS },
        gst_status: { value: null, matchMode: FilterMatchMode.EQUALS },
        wcb_status: { value: null, matchMode: FilterMatchMode.EQUALS },
        t4a_status: { value: null, matchMode: FilterMatchMode.EQUALS },
        t5_status: { value: null, matchMode: FilterMatchMode.EQUALS },
    };

    // filters
    const [globalFilter, setGlobalFilter] = useState<any>(() => {
        return localStorage.getItem(`${storagePrefix}Search`) || null;
    });

    // pagination
    const [first, setFirst] = useState<number>(() => {
        const savedFirst = localStorage.getItem(`${storagePrefix}First`);
        return savedFirst ? parseInt(savedFirst, 10) : 0;
    });
    const [rows, setRows] = useState<number>(() => {
        const savedRows = localStorage.getItem(`${storagePrefix}Rows`);
        return savedRows ? parseInt(savedRows, 10) : defaultRowOptions;
    });

    const [selectedYear, setSelectedYear] = useState<number | null>(() => {
        const savedYear = localStorage.getItem(`${storagePrefix}Year`);
        return savedYear ? parseInt(savedYear, 10) : new Date().getFullYear();
    });

    const [deleteTarget, setDeleteTarget] = useState<any>(null);
    const [deletePassword, setDeletePassword] = useState<string>('');
    const [deleteError, setDeleteError] = useState<string>('');
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false);

    const [pageLoad, setPageLoad] = useState<boolean>(false);
    const [companyList, setCompanyList] = useState<any[]>([]);
    const [masterList, setMasterList] = useState<any[]>([]);

    const [companyDialog, setCompanyDialog] = useState<boolean>(false);
    const [company, setCompany] = useState<any>({});
    const [submitted, setSubmitted] = useState<boolean>(false);
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [errors, setErrors] = useState<any>({});

    const [submitLoading, setSubmitLoading] = useState<boolean>(false);
    const [displayBusinessNo, setDisplayBusinessNo] = useState<string>('');
    const [copiedId, setCopiedId] = useState<number | null>(null);

    const [filters, setFilters] = useState<any>(() => {
        const savedFilters = localStorage.getItem(`${storagePrefix}Filters`);
        if (savedFilters) {
            try {
                return { ...emptyFilters, ...JSON.parse(savedFilters) };
            } catch (e) {
                return emptyFilters;
            }
        }
        return emptyFilters;
    });

    useEffect(() => {
        localStorage.setItem(`${storagePrefix}Filters`, JSON.stringify(filters));
    }, [filters]);

    useEffect(() => {
        if (selectedYear) localStorage.setItem(`${storagePrefix}Year`, String(selectedYear));
        else localStorage.removeItem(`${storagePrefix}Year`);
        getCompanyList();
    }, [selectedYear]);

    const openNewCompany = () => {
        setCompany({
            tax_filing: moment().format('DD/MM'),
            gst_filing: gstOption[0].value,
            gst_pick_date: moment().startOf('year').toDate(),
            wcb_filing: WCBOptions[0].value
        });
        setDisplayBusinessNo('');
        setSubmitted(false);
        setIsEdit(false);
        setErrors({});
        setCompanyDialog(true);
    };

    const editCompany = (companyData: any) => {
        const val = companyData.business_no?.toString() || '';
        let cleanVal = val.replace(/\s+/g, '').replace('RP', '').slice(0, 15);
        let displayVal = cleanVal;
        if (cleanVal.length > 9) {
            const firstNine = cleanVal.slice(0, 9);
            const rest = cleanVal.slice(9);
            displayVal = `${firstNine} RP ${rest}`;
        }
        setDisplayBusinessNo(displayVal);
        setCompany({
            ...companyData,
            business_no: cleanVal,
            gst_pick_date: companyData.gst_start_date ? moment(companyData.gst_start_date).toDate() : null
        });
        setSubmitted(false);
        setErrors({});
        setIsEdit(true);
        setCompanyDialog(true);
    };

    const companyNameBodyTemplate = (rowData: any) => {
        const companyName = rowData.company_name || '-';
        const words = companyName.trim().split(/\s+/);
        const isTruncated = words.length > 5;
        const displayName = isTruncated ? words.slice(0, 5).join(' ') + '...' : companyName;
        const targetClass = `name-tooltip-${rowData.id}`;

        return (
            <NavLink to={`/company-detail/${btoa(rowData.id)}`} state={{ companyName: rowData.company_name }} className="font-bold underline">
                <div className="avatar-div flex align-items-center justify-content-start">
                    <div className="emp-name link-name ml-2">
                        {isTruncated ? (
                            <>
                                <div className={`name ${targetClass}`} data-pr-tooltip={companyName} data-pr-position="bottom">
                                    {displayName}
                                </div>
                                <Tooltip target={`.${targetClass}`} />
                            </>
                        ) : (
                            <div className="name">{displayName}</div>
                        )}
                    </div>
                </div>
            </NavLink>
        );
    };

    const businessNoBodyTemplate = (rowData: any) => {
        const val = rowData.business_no?.toString() || '';
        const cleanVal = val.replace(/\s+/g, '').replace('RP', '').slice(0, 15);
        if (cleanVal.length > 9) {
            const firstNine = cleanVal.slice(0, 9);
            const rest = cleanVal.slice(9);
            return `${firstNine} RP ${rest}`;
        }
        return cleanVal;
    };

    const mobileNoBodyTemplate = (rowData: any) => {
        const isCopied = copiedId === rowData.id;
        const targetClass = `mobile-copy-tooltip-${rowData.id}`;
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
                <span>{rowData.mobile_no}</span>
                <span className={targetClass} style={{ cursor: "pointer", display: "flex" }}
                    onClick={async () => {
                        await navigator.clipboard.writeText(rowData.mobile_no);
                        setCopiedId(rowData.id);
                        setTimeout(() => setCopiedId(null), 5000);
                    }}
                    data-pr-tooltip={isCopied ? "Copied" : "Copy"}
                    data-pr-position="top">
                    {isCopied ? <Check size={16} color="#22c55e" /> : <Copy size={16} color="#6b7280" />}
                </span>
                <Tooltip target={`.${targetClass}`} key={isCopied ? "copied" : "copy"} />
            </div>
        );
    };

    const companyStatusBadge = (rowData: any, field: 't4a_return' | 't5_return' | 'financials' | 'document_status') => {
        const statusMap: Record<typeof field, string | undefined> = {
            t4a_return: rowData.tax_filings?.[0]?.t4a_return,
            t5_return: rowData.tax_filings?.[0]?.t5_return,
            financials: rowData.tax_filings?.[0]?.financials,
            document_status: rowData.tax_filings?.[0]?.document_status,
        };

        const status = statusMap[field];
        if (!status) return '-';

        return (
            <Badge
                severity={['Completed', 'Filed', 'Done', 'Signed', 'Sign Done', 'Received'].includes(status) ? 'success' : 'warning'}
                style={{ width: 'fit-content', cursor: 'pointer', textDecoration: 'underline', ...(status === 'N/A' && { background: 'transparent', color: 'inherit', fontWeight: 'normal', fontSize: 'inherit', padding: 0, minWidth: 'auto' }) }}
                value={status}
                onClick={() => navigate(`/company-detail/${btoa(rowData.id)}`, {
                    state: { activeIndex: 0, companyName: rowData.company_name }
                })}
            />
        );
    };

    const t4AFilingReturn = (rowData: any) => companyStatusBadge(rowData, 't4a_return');
    const t5FlilingReturn = (rowData: any) => companyStatusBadge(rowData, 't5_return');
    const financialsFilingReturn = (rowData: any) => companyStatusBadge(rowData, 'financials');
    const documentStatusFilingReturn = (rowData: any) => companyStatusBadge(rowData, 'document_status');

    const actionBodyTemplate = (rowData: any) => {
        return (
            <div className="actions">
                <Button icon="pi pi-pencil" className="common-edit-btn" tooltip="Edit Company" tooltipOptions={{ position: 'bottom' }} onClick={() => editCompany(rowData)}
                    style={{ cursor: 'pointer', textDecoration: "underline" }} />
                <Button icon="pi pi-trash" className="ml-2 p-button-danger common-delete-btn" tooltip="Delete Company" tooltipOptions={{ position: 'bottom' }} onClick={() => openDeleteDialog(rowData)}
                    style={{ cursor: 'pointer' }} />
            </div>
        );
    };


    const filingBodyTemplate = (rowData: any, field: string) => {
        const val = rowData[field];

        let status = '';
        let tabIndex = 0;

        if (field === 'tax_filing') {
            status = rowData.tax_filings?.[0]?.t2_return;
            tabIndex = 0;
        } else if (field === 'gst_filing') {
            status = rowData.gst_filings?.[0]?.status;
            tabIndex = 1;
        } else if (field === 'wcb_filing') {
            status = rowData.wcb_filings?.[0]?.status;
            tabIndex = 2;
        }

        const m = moment(val, 'DD/MM', true);
        const formattedDate = m.isValid() ? m.format('DD/MM') : val;

        if (!val && !status) return '-';

        return (
            <div className="flex flex-column align-items-center gap-2 py-2">
                <span>{formattedDate || '-'}</span>
                {status && (
                    <Badge
                        value={status}
                        severity={['Completed', 'Filed', 'Done', 'Signed', 'Sign Done', 'Received'].includes(status) ? 'success' : 'warning'}
                        style={{ cursor: 'pointer', textDecoration: field === 'wcb_filing' && status === 'N/A' ? 'none' : 'underline', ...(status === 'N/A' && { background: 'transparent', color: 'inherit', fontWeight: 'normal', fontSize: 'inherit', padding: 0, minWidth: 'auto' }) }}
                        onClick={() => navigate(`/company-detail/${btoa(rowData.id)}`, { state: { activeIndex: tabIndex, companyName: rowData.company_name } })}
                    />
                )}
            </div>
        );
    };

    const openDeleteDialog = (companyData: any) => {
        setDeleteTarget(companyData);
        setDeletePassword('');
        setDeleteError('');
    };

    const closeDeleteDialog = () => {
        if (deleteLoading) return;
        setDeleteTarget(null);
        setDeletePassword('');
        setDeleteError('');
    };

    const confirmDeleteCompany = async () => {
        if (!deletePassword.trim()) {
            setDeleteError('Password is required');
            return;
        }
        try {
            setDeleteLoading(true);
            const response = await deleteData(DELETE_COMPANY, deleteTarget.id, { password: btoa(unescape(encodeURIComponent(deletePassword))) });
            toast.current?.show({ severity: 'success', summary: 'Success', detail: response?.message || 'Company deleted successfully' });
            setDeleteTarget(null);
            setDeletePassword('');
            getCompanyList();
        } catch (error: any) {
            setDeleteError(error?.response?.data?.message || 'Failed to delete company');
        } finally {
            setDeleteLoading(false);
        }
    };

    const hideDialog = () => {
        setSubmitted(false);
        setErrors({});
        setCompanyDialog(false);
    };

    const saveCompany = async () => {
        setSubmitted(true);
        const { isError, errors } = validateCompanyForm(company);
        const gstDateMissing = company.gst_filing === 'Quarterly' && !company.gst_pick_date;
        if (gstDateMissing) errors.gst_start_date = 'Please select a date for quarterly GST';
        setErrors(errors);
        if (isError || gstDateMissing) return;

        try {
            setSubmitLoading(true);
            const payload = {
                company_name: company.company_name,
                email: company.email,
                mobile_no: company.mobile_no,
                business_no: company.business_no && company.business_no.length > 9 ? `${company.business_no.slice(0, 9)}RP${company.business_no.slice(9)}` : company.business_no,
                tax_filing: company.tax_filing,
                gst_filing: company.gst_filing,
                gst_start_date: company.gst_filing === 'Quarterly' && company.gst_pick_date
                    ? moment(company.gst_pick_date).format('YYYY-MM-DD')
                    : null,
                wcb_filing: company.wcb_filing
            };

            let response;
            if (isEdit && company.id) {
                response = await updateData(EDIT_COMPANY_DETAILS, company.id, payload);
            } else {
                response = await postAllData(ADD_COMPANY_DETAILS, payload);
            }

            if (response) {
                toast.current?.show({ severity: 'success', summary: 'Success', detail: response.message || 'Company saved successfully' });
                hideDialog();
                getCompanyList();
            }
        } catch (error: any) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: error?.response?.data?.message || 'Failed to save company' });
        } finally {
            setSubmitLoading(false);
        }
    };

    const onInputChange = (e: any, name: string) => {
        let val = e?.target?.value ?? e?.value;

        if (name === 'tax_filing' && val instanceof Date) {
            val = moment(val).format('DD/MM');
        }

        if (name === 'business_no') {
            const cleanVal = val.toString().replace(/\s+/g, '').replace('RP', '').slice(0, 15);
            let displayVal = cleanVal;
            if (cleanVal.length > 9) {
                const firstNine = cleanVal.slice(0, 9);
                const rest = cleanVal.slice(9);
                displayVal = `${firstNine} RP ${rest}`;
            }
            setDisplayBusinessNo(displayVal);
            setCompany({ ...company, business_no: cleanVal });
            setErrors({ ...errors, business_no: '' });
            return;
        }

        let _company = { ...company };
        _company[`${name}`] = val;
        if (name === 'gst_filing' && val !== 'Quarterly') {
            _company.gst_pick_date = null;
        } else if (name === 'gst_filing' && val === 'Quarterly' && !_company.gst_pick_date) {
            _company.gst_pick_date = moment().startOf('year').toDate();
        }
        setCompany(_company);
        setErrors({
            ...errors,
            [name]: '',
            ...(name === 'gst_pick_date' || name === 'gst_filing' ? { gst_start_date: '' } : {})
        });
    };

    const statusFilterTemplate = (options: any) => {
        return (
            <Dropdown
                value={options.value}
                options={CompanyFilingStatusOptions}
                onChange={(e) => options.filterCallback(e.value)}
                placeholder="Select Status"
                className="p-column-filter"
                showClear
            />
        );
    };

    const pendingFiledFilterTemplate = (options: any) => {
        return (
            <Dropdown
                value={options.value}
                options={PendingFiledStatusOptions}
                onChange={(e) => options.filterCallback(e.value)}
                placeholder="Select Status"
                className="p-column-filter"
                showClear
            />
        );
    };

    const getCompanyList = async () => {
        setPageLoad(false);
        try {
            const response = await getAllDetails(selectedYear ? `${LIST_COMPANIES}?year=${selectedYear}` : LIST_COMPANIES);
            const data = response?.data || response || [];

            const processedData = data.map((item: any) => {
                const successStates = ['Completed', 'Filed', 'Done', 'Signed', 'Sign Done'];
                return {
                    ...item,
                    tax_status: item.tax_filings?.[0]?.t2_return || '-',
                    gst_status: successStates.includes(item.gst_filings?.[0]?.status) ? 'Filed' : (item.gst_filings?.[0]?.status || '-'),
                    wcb_status: successStates.includes(item.wcb_filings?.[0]?.status) ? 'Filed' : (item.wcb_filings?.[0]?.status || '-'),
                    t4a_status: item.tax_filings?.[0]?.t4a_return || '-',
                    t5_status: item.tax_filings?.[0]?.t5_return || '-',
                }
            });

            setMasterList(processedData);

            if (globalFilter) {
                const q = globalFilter.toLowerCase().trim();
                const filtered = processedData.filter((item: any) =>
                    (item.company_name?.toLowerCase().includes(q)) ||
                    (item.email?.toLowerCase().includes(q)) ||
                    (item.mobile_no?.toString().toLowerCase().includes(q))
                );
                setCompanyList(filtered);
            } else {
                setCompanyList(processedData);
            }
            setPageLoad(true);
        } catch (error: any) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error?.response?.data?.message || 'Failed to fetch list',
            });
            setPageLoad(false);
        }
    };

    const onGlobalFilterChange = (e: any) => {
        const value = e.target.value;
        setGlobalFilter(value);
        if (!value) {
            setFirst(0);
            localStorage.setItem(`${storagePrefix}First`, '0');
            localStorage.removeItem(`${storagePrefix}Search`);
            setCompanyList(masterList);
        }
    };

    const clearAll = () => {
        setFilters({ ...emptyFilters });
        setGlobalFilter(null);
        localStorage.removeItem(`${storagePrefix}First`);
        localStorage.removeItem(`${storagePrefix}Rows`);
        localStorage.removeItem(`${storagePrefix}Search`);
        localStorage.removeItem(`${storagePrefix}Filters`);
        setFirst(0);
        setRows(defaultRowOptions);
        setCompanyList(masterList);
    };

    // Sync table and top scrollbars
    useEffect(() => {
        const tableEl = dataTableRef.current?.getElement();
        if (!tableEl) return;
        const wrapper = tableEl.querySelector('.p-datatable-scrollable-body') || tableEl.querySelector('.p-datatable-wrapper');
        const topScroll = topScrollRef.current;
        if (wrapper && topScroll) {
            const updateDummyWidth = () => {
                const table = wrapper.querySelector('table');
                if (table) {
                    const dummyContent: any = topScroll.querySelector('.dummy-content');
                    if (dummyContent) dummyContent.style.width = table.scrollWidth + 'px';
                }
            };
            updateDummyWidth();
            const handleTopScroll = (e: any) => { wrapper.scrollLeft = e.target.scrollLeft; };
            const handleBottomScroll = (e: any) => { topScroll.scrollLeft = e.target.scrollLeft; };
            topScroll.addEventListener('scroll', handleTopScroll);
            wrapper.addEventListener('scroll', handleBottomScroll);
            let resizeObserver: ResizeObserver | null = null;
            if (typeof ResizeObserver !== 'undefined' && wrapper.querySelector('table')) {
                resizeObserver = new ResizeObserver(() => updateDummyWidth());
                resizeObserver.observe(wrapper.querySelector('table'));
            }
            return () => {
                topScroll.removeEventListener('scroll', handleTopScroll);
                wrapper.removeEventListener('scroll', handleBottomScroll);
                if (resizeObserver) resizeObserver.disconnect();
            };
        }
    }, [companyList, pageLoad]);

    const leftToolbarTemplate = () => (
        <>
            <span className="block mr-2 p-input-icon-left">
                <i className="pi pi-search" />
                <InputText type="search" value={globalFilter || ''} onInput={(e) => setGlobalFilter(e.currentTarget.value)} placeholder="Search..." />
            </span>
        </>
    );

    const rightToolbarTemplate = () => (
        <>
            <Calendar
                value={selectedYear ? new Date(selectedYear, 0, 1) : null}
                onChange={(e: any) => setSelectedYear(e.value ? (e.value as Date).getFullYear() : null)}
                view="year"
                dateFormat="yy"
                placeholder="Select Year"
                showIcon
                className="mr-2"
            />
            <Button label="Add New Company" onClick={openNewCompany} className="common-primary-button mr-2" />
            <Button icon="pi pi-filter-slash" label="Clear" onClick={clearAll} className="ml-3 p-button-outlined common-p-outline-button" />
        </>
    );

    const companyDialogFooter = (
        <>
            <Button label="Cancel" icon="pi pi-times" className="p-button-secondary" onClick={hideDialog} />
            <Button label="Save" icon="pi pi-check" className="common-primary-button" onClick={saveCompany} loading={submitLoading} />
        </>
    );

    return (
        <>
            <AppBreadcrumb currentMenu="Company List" pageName="Company List" pageTitle="Company" />
            <Toast ref={toast} />
            <div className="grid crud-demo mt-4">
                <div className="col-12">
                    <div className="card">
                        <style>
                            {`
                              .purple-upload-btn:hover { background-color: #800080 !important; border-color: #800080 !important; }
                .top-scrollbar { overflow-x: auto; overflow-y: hidden; height: 20px; width: 100%; }
                .dummy-content { height: 1px; }
                                .hide-horizontal-scrollbar .p-datatable-wrapper,
                                .hide-horizontal-scrollbar .p-datatable-scrollable-body,
                                .hide-horizontal-scrollbar .p-datatable-scrollable-wrapper,
                                .hide-horizontal-scrollbar .p-datatable-scrollable-view {
                                  scrollbar-width: none !important;
                                  -ms-overflow-style: none !important;
                                }
                                .hide-horizontal-scrollbar .p-datatable-wrapper::-webkit-scrollbar:horizontal,
                                .hide-horizontal-scrollbar .p-datatable-scrollable-body::-webkit-scrollbar:horizontal,
                                .hide-horizontal-scrollbar .p-datatable-scrollable-wrapper::-webkit-scrollbar:horizontal,
                                .hide-horizontal-scrollbar .p-datatable-scrollable-view::-webkit-scrollbar:horizontal {
                                  display: none !important;
                                  height: 0 !important;
                                  width: 0 !important;
                                }
                                .p-datatable .p-datatable-thead > tr > th {
                                  white-space: normal !important;
                                  word-wrap: break-word !important;
                                  text-align: center !important;
                                
                                }
                                   .p-datatable .p-datatable-tbody > tr > td {
                                      /* justify-content: center !important; */
                                   }
                                .p-datatable .p-datatable-thead > tr > th .p-column-title {
                                  white-space: normal !important;
                                }
                                .crud-demo .p-datatable .p-datatable-wrapper .p-datatable-thead > tr > th {
                                  padding : 5px !important;
                                }
                                .crud-demo .p-datatable .p-datatable-wrapper .p-datatable-tbody > tr > td {
                                  padding : 5px !important;
                                }
                            `}
                        </style>
                        <Toolbar left={leftToolbarTemplate} right={rightToolbarTemplate}></Toolbar>

                        <div ref={topScrollRef} className="top-scrollbar">
                            <div className="dummy-content"></div>
                        </div>

                        {pageLoad ? (
                            <DataTable
                                ref={dataTableRef}
                                value={companyList || []}
                                showGridlines
                                dataKey="id"
                                globalFilter={globalFilter}
                                filters={filters}
                                responsiveLayout="scroll"
                                stripedRows
                                rows={rows}
                                first={first}
                                onPage={(e: any) => {
                                    setFirst(e.first);
                                    setRows(e.rows);
                                    localStorage.setItem(`${storagePrefix}First`, String(e.first));
                                    localStorage.setItem(`${storagePrefix}Rows`, String(e.rows));
                                }}
                                paginator={(companyList?.length || 0) > 0}
                                rowsPerPageOptions={defaultPageRowOptions}
                                className="datatable-responsive p-datatable-sm hide-horizontal-scrollbar"
                                paginatorTemplate={paginatorLinks}
                                currentPageReportTemplate={showingEntries + " Company"}
                                emptyMessage="No company list found."
                                removableSort
                                scrollable
                                filterDisplay="menu"
                                globalFilterFields={['company_name', 'email', "mobile_no"]}
                            >
                                {CompanyListColumns.map((col) => {
                                    if (col.field === 'action') {
                                        return <Column key={col.field} field={col.field} header={col.header} body={actionBodyTemplate} style={{ minWidth: '8rem', display: "flex", justifyContent: 'center' }} />;
                                    }
                                    if (col.field === 'company_name') {
                                        return <Column key={col.field} field={col.field} header={col.header} body={companyNameBodyTemplate} style={{ minWidth: '16rem', zIndex: 1, display: "flex", justifyContent: "flex-start" }} filter filterPlaceholder="Search by Company Name" frozen alignFrozen="left" />;
                                    }
                                    if (col.field === 'business_no') {
                                        return <Column key={col.field} field={col.field} header={col.header} body={businessNoBodyTemplate} style={{ minWidth: '11rem', display: "flex", justifyContent: "center" }} />;
                                    }
                                    if (col.field === 'mobile_no') {
                                        return <Column key={col.field} field={col.field} header={col.header} body={mobileNoBodyTemplate} style={{ minWidth: '9rem', display: "flex", justifyContent: "center" }} />;
                                    }
                                    if (col.field === 'tax_filing' || col.field === 'gst_filing' || col.field === 'wcb_filing') {
                                        const isTax = col.field === 'tax_filing';
                                        const filterField = isTax ? 'tax_status' : col.field === 'gst_filing' ? 'gst_status' : 'wcb_status';
                                        return <Column key={col.field} field={col.field} filterField={filterField} header={col.header} body={(rowData: any) => filingBodyTemplate(rowData, col.field)} style={{ minWidth: '9rem', display: "flex", justifyContent: "center" }} filter filterElement={isTax ? statusFilterTemplate : pendingFiledFilterTemplate} showFilterMatchModes={false} />;
                                    }
                                    if (col.field === 't4a_return') {
                                        return <Column key={col.field} field={col.field} filterField="t4a_status" header={col.header} body={t4AFilingReturn} style={{ minWidth: '8rem', display: "flex", justifyContent: "center" }} />;
                                    }
                                    if (col.field === 't5_return') {
                                        return <Column key={col.field} field={col.field} filterField="t5_status" header={col.header} body={t5FlilingReturn} style={{ minWidth: '8rem', display: "flex", justifyContent: "center" }} />;
                                    }
                                    if (col.field === 'financials') {
                                        return <Column key={col.field} field={col.field} header={col.header} body={financialsFilingReturn} style={{ minWidth: '8rem', display: "flex", justifyContent: "center" }} />;
                                    }
                                    if (col.field === 'document_status') {
                                        return <Column key={col.field} field={col.field} header={col.header} body={documentStatusFilingReturn} style={{ minWidth: '9rem', display: "flex", justifyContent: "center" }} />;
                                    }
                                    const minWidth = col.field === 'address' ? '20rem' : (col.field === 'email' ? '14rem' : '14rem');
                                    return (
                                        <Column
                                            key={col.field}
                                            field={col.field}
                                            header={col.header}
                                            filter={col.field === 'email'}
                                            filterPlaceholder={`Search by ${col.header}`}
                                            style={{ minWidth: minWidth, display: "flex", justifyContent: "center" }}
                                        />
                                    );
                                })}
                            </DataTable>
                        ) : (
                            <DataTable value={Skeletonitems} className="hide-horizontal-scrollbar">
                                {CompanyListColumns.map((col) => (
                                    <Column key={col.field} field={col.field} header={col.header} body={SkeletonbodyTemplate} style={{ minWidth: "14rem" }} />
                                ))}
                            </DataTable>
                        )}
                    </div>
                </div>
            </div>

            <Dialog
                visible={!!deleteTarget}
                style={{ width: '450px' }}
                header="Delete Company"
                modal
                className="p-fluid mx-3 lg:mx-0"
                onHide={closeDeleteDialog}
                footer={
                    <>
                        <Button label="Cancel" icon="pi pi-times" className="p-button-secondary" onClick={closeDeleteDialog} disabled={deleteLoading} />
                        <Button label="Delete" icon="pi pi-trash" className="p-button-danger" onClick={confirmDeleteCompany} loading={deleteLoading} />
                    </>
                }
            >
                <p style={{ color: 'red', fontWeight: 600 }}>
                    Warning: This will permanently delete all data of <b>{deleteTarget?.company_name}</b> (filings, documents, notes and related records). This action cannot be undone.
                </p>
                <div className="field mt-4">
                    <label htmlFor="delete_password">Enter your password to confirm <span className="required">*</span></label>
                    <Password
                        inputId="delete_password"
                        value={deletePassword}
                        onChange={(e) => { setDeletePassword(e.target.value); setDeleteError(''); }}
                        feedback={false}
                        toggleMask
                        autoComplete="new-password"
                        placeholder="Enter Password"
                        className={deleteError ? 'p-invalid' : ''}
                        onKeyDown={(e) => { if (e.key === 'Enter') confirmDeleteCompany(); }}
                    />
                    <small className="p-invalid-txt">{deleteError}</small>
                </div>
            </Dialog>

            <Dialog visible={companyDialog} style={{ width: '660px' }} header={isEdit ? "Edit Company Details" : "Add Company Details"} modal className="p-fluid mx-3 lg:mx-0" footer={companyDialogFooter} onHide={hideDialog}>
                <div className="grid">
                    <div className="field col-12 md:col-6">
                        <label htmlFor="company_name">Company Name <span className="required">*</span></label>
                        <InputText id="company_name" autoComplete="off" value={company?.company_name || ''} onChange={(e) => onInputChange(e, 'company_name')} placeholder="Enter Company Name" className={errors.company_name && 'p-invalid'} required autoFocus />
                        <small className="p-invalid-txt">{errors.company_name}</small>
                    </div>
                    <div className="field col-12 md:col-6">
                        <label htmlFor="business_number">Business Number <span className="required">*</span></label>
                        <InputText
                            id="business_number"
                            value={displayBusinessNo || ''}
                            onChange={(e) => onInputChange(e, 'business_no')}
                            autoComplete="off"
                            placeholder="Enter Business Number"
                            maxLength={17}
                            className={errors.business_no && 'p-invalid'}
                        />
                        <small className="p-invalid-txt">{errors.business_no}</small>
                    </div>
                    <div className="field col-12 md:col-6">
                        <label htmlFor="email">Email <span className="required">*</span></label>
                        <InputText id="email" value={company?.email || ''} onChange={(e) => onInputChange(e, 'email')} placeholder="Enter Email" className={errors.email && 'p-invalid'} required />
                        <small className="p-invalid-txt">{errors.email}</small>
                    </div>
                    <div className="field col-12 md:col-6">
                        <label htmlFor="mobile_number">Mobile Number <span className="required">*</span></label>
                        <InputText id="mobile_number" autoComplete="off" maxLength={10} value={company?.mobile_no || ''} onChange={(e) => onInputChange(e, 'mobile_no')} placeholder="Enter Mobile Number" className={errors.mobile_no && 'p-invalid'} required />
                        <small className="p-invalid-txt">{errors.mobile_no}</small>
                    </div>
                    <div className="field col-12 md:col-6">
                        <label htmlFor="tax_filing">When do you want to file your Tax?</label>
                        <Calendar
                            id="tax_filing"
                            value={company?.tax_filing ? moment(company.tax_filing, 'DD/MM').toDate() : null}
                            onChange={(e) => onInputChange(e, 'tax_filing')}
                            dateFormat="dd/mm"
                            placeholder="Select Date and Month"
                            showIcon
                            disabled={isEdit}
                        />
                    </div>

                    <div className="field col-12 md:col-6">
                        <label>When do you want to file your WCB?</label>
                        <div className="flex gap-4 mt-2 flex-wrap">
                            {WCBOptions.map(opt => (
                                <div className="flex align-items-center" key={opt.value}>
                                    <RadioButton inputId={`wcb_filing_${opt.value}`} name="wcb_filing" value={opt.value} onChange={(e) => onInputChange(e, 'wcb_filing')} checked={company?.wcb_filing === opt.value} />
                                    <label htmlFor={`wcb_filing_${opt.value}`} className="ml-2">{opt.label}</label>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="field col-12 md:col-6">
                        <label>When do you want to file your GST?</label>
                        <div className="flex gap-4 mt-2 flex-wrap">
                            {gstOption.map(opt => (
                                <div className="flex align-items-center" key={opt.value}>
                                    <RadioButton inputId={`gst_filing_${opt.value}`} name="gst_filing" value={opt.value} onChange={(e) => onInputChange(e, 'gst_filing')} checked={company?.gst_filing === opt.value} />
                                    <label htmlFor={`gst_filing_${opt.value}`} className="ml-2">{opt.label}</label>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Always rendered (hidden when not Quarterly) so the dialog height doesn't jump */}
                    <div className="field col-12 md:col-6" style={{ visibility: company?.gst_filing === 'Quarterly' ? 'visible' : 'hidden', minHeight: '5.5rem' }}>
                            <label htmlFor="gst_pick_date">GST Quarter Start Date <span className="required">*</span></label>
                            <Calendar
                                id="gst_pick_date"
                                value={company?.gst_pick_date || null}
                                onChange={(e) => onInputChange(e, 'gst_pick_date')}
                                dateFormat="dd/mm/yy"
                                placeholder="Select Date"
                                showIcon
                                className={errors.gst_start_date && 'p-invalid'}
                            />
                            <small className="p-invalid-txt">{errors.gst_start_date}</small>
                    </div>

                </div>
            </Dialog>
        </>
    );
};

export default CompanyList;